import { useEffect, useRef, useCallback } from 'react';

const RENEW_BEFORE_MS = 5 * 60 * 1000; // intentar renovar hasta 5 min antes del vencimiento
const FALLBACK_DELAY_MS = 10 * 60 * 1000; // si no se puede leer el exp, reintentar en 10 min
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 10 * 1000; // 10 s base para backoff exponencial

/**
 * Decodifica el claim `exp` (segundos) de un JWT y lo devuelve en milisegundos.
 * Devuelve null si no se puede leer.
 */
function getTokenExpMs(jwt) {
    try {
        const payload = jwt && jwt.split('.')[1];
        if (!payload) return null;
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const decoded = JSON.parse(atob(padded));
        return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
    } catch {
        return null;
    }
}

/**
 * Calcula el retraso (ms) hasta la próxima renovación a partir del vencimiento REAL
 * del token. Usa el `exp` del JWT; si no se puede leer, cae al campo `expiracion`.
 * Renueva 5 min antes, o a la mitad de la vida restante si el TTL es corto.
 */
function computeRenewDelay(tokenStr, expiracion) {
    let expMs = getTokenExpMs(tokenStr);
    if (expMs == null && expiracion) {
        const t = new Date(expiracion).getTime();
        if (!Number.isNaN(t)) expMs = t;
    }
    if (expMs == null) return FALLBACK_DELAY_MS;

    const remaining = expMs - Date.now();
    if (remaining <= 0) return 0; // ya expiró → renovar de inmediato

    const lead = Math.min(RENEW_BEFORE_MS, remaining / 2);
    return Math.max(remaining - lead, 0);
}

/**
 * Maneja la renovación automática del token de forma silenciosa.
 *
 * @param {object}   options
 * @param {React.MutableRefObject<string>} options.tokenRef  - ref que siempre contiene el token actual
 * @param {function} options.setToken   - guarda el token renovado en sessionStorage y state
 * @param {string}   options.endpoint  - base URL del API (REACT_APP_SERVICE_ENDPOINT_NE)
 */
export function useTokenRenewal({ tokenRef, setToken, endpoint }) {
    const timerRef      = useRef(null);
    const retryCountRef = useRef(0);
    const abortRef      = useRef(null);
    const isMountedRef  = useRef(true);

    const scheduleRenewal = useCallback((delayMs) => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(doRenew, delayMs); // eslint-disable-line no-use-before-define
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const doRenew = useCallback(async () => {
        if (!isMountedRef.current) return;

        const stored = sessionStorage.getItem('token');
        if (!stored) return; // sesión ya cerrada

        // Cancelar fetch anterior si aún está en vuelo
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        try {
            const res = await fetch(`${endpoint}/usuarios/renovartoken`, {
                method: 'GET',
                signal: abortRef.current.signal,
                headers: {
                    authorization: `Bearer ${tokenRef.current}`,
                    Accept: 'application/json',
                },
            });

            if (!isMountedRef.current) return;

            // El backend redirige a /Account/Login (302) cuando el token no es válido,
            // en vez de devolver 401. fetch sigue el redirect, así que lo detectamos aquí.
            const redirigioALogin = res.redirected || res.url.includes('/Account/Login');

            if (res.status === 401 || res.status === 403 || redirigioALogin) {
                // Token inválido o expirado — logout limpio
                sessionStorage.removeItem('token');
                window.location.reload();
                return;
            }

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const data = await res.json();
            if (!data?.token) throw new Error('Respuesta sin campo token');

            // Éxito: actualizar token y reagendar según el vencimiento REAL del nuevo token
            retryCountRef.current = 0;
            setToken(data);
            tokenRef.current = data.token;
            scheduleRenewal(computeRenewDelay(data.token, data.expiracion));

        } catch (err) {
            if (err.name === 'AbortError') return; // fetch cancelado intencionalmente
            if (!isMountedRef.current) return;

            retryCountRef.current += 1;

            if (retryCountRef.current >= MAX_RETRIES) {
                // Máximos reintentos consecutivos: logout preventivo
                sessionStorage.removeItem('token');
                window.location.reload();
                return;
            }

            // Backoff exponencial: 10 s, 20 s, 40 s
            const delay = RETRY_BASE_MS * Math.pow(2, retryCountRef.current - 1);
            scheduleRenewal(delay);
        }
    }, [endpoint, tokenRef, setToken, scheduleRenewal]);

    useEffect(() => {
        isMountedRef.current = true;

        // Agendar la primera renovación según el vencimiento real del token guardado
        const stored = sessionStorage.getItem('token');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                scheduleRenewal(computeRenewDelay(parsed.token, parsed.expiracion));
            } catch {
                scheduleRenewal(FALLBACK_DELAY_MS);
            }
        }

        // Page Visibility API: renovar inmediatamente al volver al foco
        // (evita tokens expirados tras suspensión del SO o tab en background)
        const handleVisibility = () => {
            if (document.visibilityState !== 'visible') return;
            const s = sessionStorage.getItem('token');
            if (!s) return;
            try {
                const parsed = JSON.parse(s);
                let expMs = getTokenExpMs(parsed.token);
                if (expMs == null && parsed.expiracion) expMs = new Date(parsed.expiracion).getTime();
                if (expMs != null && expMs - Date.now() < RENEW_BEFORE_MS) scheduleRenewal(0);
            } catch {
                scheduleRenewal(0);
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            isMountedRef.current = false;
            clearTimeout(timerRef.current);
            if (abortRef.current) abortRef.current.abort();
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [scheduleRenewal]);
}
